/**
 * Onboarding and "what do I do now" (order 762, presentation only — never touches the simulation):
 *
 *  - the intro: after New game, a short note from Mags (three pages, skippable) that says who you are and
 *    where to go first, then lands in the book as an unread note so the bleeper invites you to open it;
 *  - the "what next" tracker: whenever no job is running, the objective clipping (tagged NEXT) says the
 *    next sensible thing (`nextStep` over the mission service's job board), with distance, a minimap
 *    marker pinned to the rim, a world pointer over the target and an edge-of-screen arrow when it's off
 *    screen. A place marked in the book wins until you get there;
 *  - contextual tips: one short slip at a time, only when relevant and only until done (persisted in
 *    `bh.onboarding.v1`), worded for keys or touch, off in Settings → Tips and pointers;
 *  - online, the first join of each character gets a short welcome to the shared city.
 */
import { allowed } from '../ui/attention';
import { horizonLine, nearestRung, tracksFor } from '../../shared/contacts';
import { playerLockupPoi } from '../economy/playerLockup';
import './onboarding.css';
import { Vector3 } from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { touchUi } from '../input/touch';
import { el, setText, toggle } from '../ui/dom';
import { bindingLabel, formatDistance, touchWording } from '../ui/format';
import type { MapMarker } from '../ui/uiTypes';
import { LOCKUP_PRICE, LOCKUP_UNLOCK_ID } from '../economy/wallet';
import { nextStep, type NextStep, type OnlineFacts, type Waypoint } from './nextStep';
import { invites } from '../online-ui/inviteUi';
import { firstHour } from '../online-ui/firstHour';
import { FREIGHT } from '../../shared/messages/activities';
import { edgePointer, type Frame, type PointerPlace } from './pointer';
import { jobUp, pickRouteGoal, type RouteGoal } from './routeGoal';
import { BASIC_TIPS, TipEngine, type TipFacts } from './tips';
import { NoteCard, type NotePage } from './noteCard';
import { cssSize } from '../render/cssSize';

export interface OnboardingService {
  /** Settings → Tips and pointers. */
  readonly tipsSetting: { get(): boolean; set(on: boolean): void };
  /** The "what next" step right now (null while a job is running). */
  next(): NextStep | null;
  /** A place the player marked (from the book or a tap on the full map); null clears it. */
  setWaypoint(w: Waypoint | null): void;
  readonly waypoint: Waypoint | null;
  /**
   * Where the route arrows and the screen pointer lead (routeGoal.ts): the running job's next stop, or the
   * place you chose; null = no arrows. Worked out once a frame, so both always agree.
   */
  readonly goal: RouteGoal | null;
  /** The intro note (or the online welcome) is on screen. */
  readonly introOpen: boolean;
}

declare module '../core/services' {
  interface ServiceMap {
    onboarding: OnboardingService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Onboarding read-out: intro, tip on screen, tips done, next step, pointer. */
    onboardingState(): Record<string, unknown>;
    /** Turn the intro / welcome note over (or skip it with 'skip'). */
    onboardingNote(action?: string): void;
    /** Forget onboarding progress (tips done, welcomes) — tests. */
    onboardingReset(): void;
    /** Choose a place to route to (as the map's Route here does), or clear the route with no arguments — tests. */
    setRoute(x?: number, z?: number, label?: string): void;
  }
}

const STORE_KEY = 'bh.onboarding.v1';
const ARRIVE_M = 9;

interface Stored {
  v: 1;
  tips: string[];
  tipsOff: boolean;
  welcomed: string[];
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The intro: original writing, Mags' voice. The last page points at the book and at her door. */
export function introPages(touch: boolean): NotePage[] {
  return [
    {
      kicker: 'A note, pushed under your door',
      heading: 'Low Borough, 1970',
      lines: [
        'You’ve been away. The manor didn’t wait for you.',
        'Forty quid in your pocket, a name nobody wants to hear, and one friend left who’ll still open the door to you.',
      ],
    },
    {
      kicker: 'In pencil, on the back of a beer mat',
      heading: 'Come and see me',
      lines: [
        'The Crooked Anchor on Coalgate. Back-room door, round the side — don’t come through the bar.',
        'I might have work for somebody who can drive and keep their mouth shut.',
      ],
      sign: '— M.',
    },
    {
      kicker: 'Your little black book',
      heading: 'Check your book',
      lines: [
        touch
          ? 'Jobs, messages and who’s who all go in your book — tap the BOOK knob, top right. The bleeper buzzes when somebody gets in touch.'
          : 'Jobs, messages and who’s who all go in your book — press I. The bleeper top right buzzes when somebody gets in touch.',
        'First things first: go and see Mags. The note top left says where, and the arrow on screen points the way.',
      ],
    },
  ];
}

export function welcomePages(touch: boolean, city: string): NotePage[] {
  return [
    {
      kicker: 'The shared city',
      heading: `You’re in ${city}`,
      lines: [
        'Everybody you see on these streets is another player. The city keeps the books: what you earn here stays, and what you lose is gone.',
        'Cash in your pocket can be taken off you. Bank it.',
      ],
    },
    {
      kicker: 'Your first evening',
      heading: 'Three things tonight',
      lines: [],
      bullets: touch
        ? [
            'Halloran’s lent you a car — follow the arrow to it. Tap USE where it says “Get in”: “Steal” is somebody else’s.',
            'Take a job off a board — phone boxes, pubs, Halloran’s. Tap USE at the door.',
            'Bank what you earn — at the bank or a cashpoint.',
          ]
        : [
            'Halloran’s lent you a car — follow the arrow to it. Press E where it says “Get in”: “Steal” is somebody else’s.',
            'Take a job off a board — phone boxes, pubs, Halloran’s. E at the door.',
            'Bank what you earn — at the bank or a cashpoint.',
          ],
    },
    {
      kicker: 'Your little black book',
      heading: 'Everything is in the book',
      lines: [
        touch
          ? 'Tap the BOOK knob (top right) for jobs, messages, your crew and your record. New pages open in it as you need them.'
          : 'Press I for your book: jobs, messages, your crew and your record. New pages open in it as you need them.',
        'After the first three: Sid’s back room, a crew, Night Freight, a firm of your own. The note top left always says what’s next.',
      ],
    },
  ];
}

export function createOnboardingSystem(): System {
  let ctx: GameContext;
  const tips = new TipEngine();
  let stored: Stored = { v: 1, tips: [], tipsOff: false, welcomed: [] };
  let waypoint: Waypoint | null = null;
  /** Clock (s) when the mark was set, and when the running job began (null: none) — routeGoal.ts. */
  let waypointAt = 0;
  let jobSince: number | null = null;
  let goal: RouteGoal | null = null;
  let clock = 0;
  let step: NextStep | null = null;
  let note: NoteCard | null = null;
  let noteKind: 'intro' | 'welcome' | null = null;
  let introPending = false;
  let welcomePending: string | null = null;
  // tip facts, counted this session
  let walked = 0;
  let ran = 0;
  let driven = 0;
  let playTime = 0;
  let enteredCar = false;
  let mapOpened = false;
  let bookOpened = false;
  let zoomChanged = false;
  let nearCar = false;
  let nearFence = false;
  let probeTimer = 0;
  const last = { x: 0, z: 0, ok: false };
  // DOM
  let layer: HTMLElement | null = null;
  let tipEl: HTMLElement | null = null;
  let tipText: HTMLElement | null = null;
  let tipShown = '';
  let ptr: HTMLElement | null = null;
  let ptrDist: HTMLElement | null = null;
  let ptrLabel: HTMLElement | null = null;
  let ptrState: { visible: boolean; onScreen: boolean; x: number; y: number; distance: number | null; kind?: string } = { visible: false, onScreen: false, x: 0, y: 0, distance: null };
  /** The pointer's look: 'job' (red), 'mark' (plum) or 'next' (paper). */
  let ptrLook = 'next';
  const proj = new Vector3();
  const offs: (() => void)[] = [];
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  let padPrev = { a: false, b: false };

  const ui = () => ctx.services.tryGet('ui');
  const online = (): boolean => !!ctx.services.tryGet('net')?.online;

  function save(): void {
    try {
      storage()?.setItem(STORE_KEY, JSON.stringify(stored));
    } catch {
      /* not kept */
    }
  }

  function load(): boolean {
    try {
      const raw = storage()?.getItem(STORE_KEY);
      if (!raw) return false;
      const d = JSON.parse(raw) as Partial<Stored>;
      stored = {
        v: 1,
        tips: Array.isArray(d.tips) ? d.tips.filter((x): x is string => typeof x === 'string').slice(0, 50) : [],
        tipsOff: d.tipsOff === true,
        welcomed: Array.isArray(d.welcomed) ? d.welcomed.filter((x): x is string => typeof x === 'string').slice(-40) : [],
      };
      return true;
    } catch {
      return false;
    }
  }

  function markTipsDone(ids: string[]): void {
    let changed = false;
    for (const id of ids) {
      tips.done.add(id);
      if (!stored.tips.includes(id)) {
        stored.tips.push(id);
        changed = true;
      }
    }
    if (changed) save();
  }

  // ------------------------------------------------------------------ notes (intro / welcome)

  function takeControl(): void {
    ctx.services.tryGet('player')?.controls.acquire('onboarding');
  }

  function giveControl(): void {
    ctx.services.tryGet('player')?.controls.drop('onboarding');
  }

  function showIntro(): void {
    const root = ctx.uiRoot?.querySelector('.bh-ui') as HTMLElement | null;
    if (!root) return;
    introPending = false;
    noteKind = 'intro';
    takeControl();
    ctx.services.tryGet('audio')?.ui('whoosh');
    note = new NoteCard(
      root,
      introPages(touchUi.enabled),
      touchUi.enabled,
      () => {
        ctx.services.tryGet('audio')?.ui('type');
        // Page two is Mags' door: let the camera glance at it.
        if (note?.page === 1) {
          const mags = ctx.services.tryGet('missions')?.board().find((j) => j.kind === 'story' && j.available);
          if (mags) ctx.services.tryGet('cameraRig')?.cinematic({ x: mags.giver.x, z: mags.giver.z }, 3.2);
        }
      },
      () => {
        note = null;
        noteKind = null;
        giveControl();
        ctx.services.tryGet('cameraRig')?.setOverride(null);
        const pages = introPages(touchUi.enabled);
        // Unread (the BOOK knob's badge invites a look), but without the bleep: the slip "Come and see me. Tap
        // the BOOK to read it." used to land the moment the note closed, over the NEXT clipping saying the same.
        ctx.services.tryGet('book')?.post({
          quiet: true,
          key: 'intro',
          from: 'Mags Delaney',
          kind: 'story',
          subject: 'Come and see me',
          text: [...pages[0]!.lines, ...pages[1]!.lines, 'Back-room door. Don’t keep me waiting. — M.'].join('\n\n'),
          ...(step?.target ? { action: { label: 'Mark the Crooked Anchor on the map', mark: step.target } } : {}),
        });
      },
      'intro',
    );
  }

  function showWelcome(city: string, who: string): void {
    const root = ctx.uiRoot?.querySelector('.bh-ui') as HTMLElement | null;
    if (!root) return;
    welcomePending = null;
    noteKind = 'welcome';
    ctx.services.tryGet('audio')?.ui('whoosh');
    note = new NoteCard(
      root,
      welcomePages(touchUi.enabled, city),
      touchUi.enabled,
      () => ctx.services.tryGet('audio')?.ui('type'),
      () => {
        note = null;
        noteKind = null;
        if (!stored.welcomed.includes(who)) stored.welcomed.push(who);
        save();
        ctx.services.tryGet('book')?.post({
          quiet: true,
          key: `welcome:${who}`,
          from: 'The city',
          kind: 'system',
          subject: `Welcome to ${city}`,
          text: 'Halloran’s lent you a car: it’s parked where you started, and it’s on the Your motor page. Job boards are at phone boxes, pubs and Halloran’s. Bank what you earn. The rest — Sid’s, crews, Night Freight, gangs, premises — opens in your book as you get to it.',
          action: { label: 'See the jobs', tab: 'jobs' },
        });
      },
      'welcome',
    );
  }

  function noteAction(a: 'next' | 'skip'): void {
    if (!note) return;
    if (a === 'skip') note.finish(true);
    else note.next();
  }

  // ------------------------------------------------------------------ the tracker

  function computeStep(): NextStep | null {
    const missions = ctx.services.tryGet('missions');
    const player = ctx.services.tryGet('player');
    const layout = ctx.services.tryGet('layout');
    const lockup = playerLockupPoi(layout?.pois ?? []);
    const boards: Waypoint[] = [];
    if (online() && layout) {
      for (const p of layout.pois) {
        if (p.kind === 'phonebox' || p.kind === 'pub' || p.kind === 'garage') boards.push({ x: p.pos.x, z: p.pos.z, label: p.name });
      }
    }
    return nextStep({
      missionActive: !!missions?.active,
      heat: ctx.state.heat,
      waypoint,
      online: online(),
      jobs: missions?.board() ?? [],
      money: ctx.services.tryGet('economy')?.balance ?? ctx.state.money,
      lockupOwned: !!ctx.services.tryGet('save')?.hasUnlock(LOCKUP_UNLOCK_ID),
      lockupPrice: LOCKUP_PRICE,
      lockup: lockup ? { x: lockup.pos.x, z: lockup.pos.z, label: lockup.name } : null,
      player: player ? { x: player.pos.x, z: player.pos.z } : { x: 0, z: 0 },
      boards,
      ...(online() ? { street: streetFacts(layout?.pois ?? []) } : {}),
      ...(!online() ? { horizon: offlineHorizon() } : {}),
    });
  }

  /** The contact ladders' next rung for the repeat work offline (WP-X1): Halloran's exports, Lenny's street jobs. */
  function offlineHorizon(): { export?: string; street?: string } {
    const svc = ctx.services.tryGet('contacts');
    if (!svc) return {};
    const st = svc.standing();
    const ex = horizonLine('des', st, 'offline');
    const sj = horizonLine('lenny', st, 'offline');
    return { ...(ex ? { export: ex } : {}), ...(sj ? { street: sj } : {}) };
  }

  /** Online, past the first-evening ladder: the contact nearest its next rung (WP-X1). */
  function onlineHorizon(): string | undefined {
    const svc = ctx.services.tryGet('contacts');
    if (!svc || svc.mode !== 'online') return undefined;
    const st = svc.standing();
    const t = nearestRung(tracksFor('online'), st, 'online');
    return (t && horizonLine(t, st, 'online')) || undefined;
  }

  /** What the online store says about you, for the tracker's online ladder (invites, crew, cash). */
  function streetFacts(pois: readonly { id: string; name: string; pos: { x: number; z: number } }[]): OnlineFacts | undefined {
    const game = ctx.services.tryGet('online');
    if (!game?.active) return undefined;
    const st = game.store.state;
    const me = ctx.services.tryGet('net')?.session?.character.id;
    const pick = invites.choose(game.now());
    const gangInv = pick?.kind === 'gang' ? st.invites.find((i) => i.id === pick.id) : undefined;
    const crewInv = pick?.kind === 'crew' ? invites.liveCrew(game.now()) : null;
    const wp = (id: string): Waypoint | undefined => {
      const p = pois.find((x) => x.id === id);
      return p ? { x: p.pos.x, z: p.pos.z, label: p.name } : undefined;
    };
    const pier = wp(FREIGHT.pierPoi);
    const bank = wp('bank-savings');
    const ladder = firstHour.ladderFacts();
    return {
      cash: st.econ?.cash ?? 0,
      busy: !!st.econ?.job || (!!st.freight && st.freight.status === 'active'),
      invite: gangInv ? { kind: 'gang', from: gangInv.by } : crewInv ? { kind: 'crew', from: crewInv.from } : null,
      crewOnline: st.crew ? st.crew.members.filter((m) => m.online || m.cid === me).length : 0,
      inGang: !!st.gang,
      touch: touchUi.enabled,
      ...(pier ? { pier } : {}),
      ...(bank ? { bank } : {}),
      ...(ladder ? { ladder } : {}),
      ...(onlineHorizon() ? { horizon: onlineHorizon()! } : {}),
    };
  }

  /**
   * Your mark (a plum flag, the route leads there) and the "what next" suggestion (a dashed ring round the
   * place — no arrows until you choose it). Never an `objective`: that is the running job's alone.
   */
  function markerSource(out: MapMarker[]): void {
    if (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused') return;
    if (waypoint) out.push({ x: waypoint.x, z: waypoint.z, kind: 'mark', pin: true, label: waypoint.label });
    const t = step?.target;
    if (t && step!.kind !== 'waypoint') out.push({ x: t.x, z: t.z, kind: 'next', pin: true, label: t.label });
  }

  /** A job is on: offline the mission runner, online a courier job / freight run, or any job objective up. */
  function jobRunning(): boolean {
    if (ctx.services.tryGet('missions')?.active) return true;
    const game = ctx.services.tryGet('online');
    if (game?.active) {
      const st = game.store.state;
      if (st.econ?.job || (st.freight && st.freight.status === 'active')) return true;
    }
    return jobUp(ui()?.markers ?? []);
  }

  function updateGoal(): void {
    const job = jobRunning();
    if (!job) jobSince = null;
    else if (jobSince === null) jobSince = clock;
    const player = ctx.services.tryGet('player');
    const mark = waypoint ? { x: waypoint.x, z: waypoint.z, label: waypoint.label, at: waypointAt } : null;
    goal = player ? pickRouteGoal(ui()?.markers ?? [], mark, jobSince, player.pos, goal) : null;
  }

  /**
   * On a phone the paper notes (the objective clipping with the toasts under it) stand in the frame's top
   * band: a pill pinned there slides down under them rather than over the words. Measured every few
   * frames; nothing to do while the column is empty or the pointer hangs over its target.
   */
  let notesRect: DOMRect | null = null;
  let notesTick = 0;
  function clearOfNotes(p: PointerPlace): PointerPlace {
    if (!touchUi.enabled || p.onScreen) return p;
    if (notesTick-- <= 0) {
      notesTick = 10;
      const n = ctx.uiRoot?.querySelector('.hud-notes');
      notesRect = n ? n.getBoundingClientRect() : null;
    }
    const r = notesRect;
    if (!r || r.height === 0) return p;
    const reach = 40; // half the pill, roughly
    return p.x > r.left - reach && p.x < r.right + reach && p.y < r.bottom + 14 ? { ...p, y: r.bottom + 14 } : p;
  }

  /** Safe frame for the pointer: clear of the minimap / clipping / knobs (top) and the thumbs (bottom). */
  function frame(w: number, h: number): Frame {
    return touchUi.enabled
      ? { left: w * 0.16, right: w * 0.16, top: h * 0.3, bottom: h * 0.36 }
      : { left: w * 0.1, right: w * 0.15, top: h * 0.24, bottom: h * 0.22 };
  }

  /**
   * The screen pointer leads where the route arrows do (the goal: red for the job, plum for your mark); with
   * no goal, to the "what next" suggestion in paper — nothing on the road then, so the two never disagree.
   */
  function updatePointer(): void {
    if (!ptr || !ctx.render) return;
    const sug = step?.target && step.kind !== 'waypoint' ? step.target : null;
    const t = goal ?? sug;
    const look = goal ? goal.kind : 'next';
    if (look !== ptrLook) {
      ptr.classList.remove(`k-${ptrLook}`);
      ptr.classList.add(`k-${look}`);
      ptrLook = look;
    }
    const player = ctx.services.tryGet('player');
    const u = ui();
    const show = !!t && !!player && !!u?.free && !u.cinematic && !note && !moduleCardUp();
    const d = t && player ? Math.hypot(t.x - player.pos.x, t.z - player.pos.z) : null;
    if (!show || d === null || d < 4) {
      if (ptrState.visible) toggle(ptr, 'show', false);
      ptrState = { visible: false, onScreen: false, x: 0, y: 0, distance: d };
      return;
    }
    const cam = ctx.camera;
    cam.updateMatrixWorld();
    proj.set(t!.x, 2.6, t!.z);
    proj.applyMatrix4(cam.matrixWorldInverse);
    const behind = proj.z > 0;
    proj.applyMatrix4(cam.projectionMatrix);
    const css = cssSize(ctx.render.canvas);
    const W = css.w || window.innerWidth;
    const H = css.h || window.innerHeight;
    const sx = ((proj.x + 1) / 2) * W;
    const sy = ((1 - proj.y) / 2) * H;
    const p = clearOfPapers(clearOfNotes(edgePointer(sx, sy, behind, W, H, frame(W, H))));
    ptr.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
    ptr.style.setProperty('--a', `${((p.angle * 180) / Math.PI).toFixed(1)}deg`);
    toggle(ptr, 'show', true);
    toggle(ptr, 'edge', !p.onScreen);
    setText(ptrDist!, formatDistance(d));
    setText(ptrLabel!, t!.label);
    ptrState = { visible: true, onScreen: p.onScreen, x: Math.round(p.x), y: Math.round(p.y), distance: Math.round(d), kind: look };
  }

  // ------------------------------------------------------------------ tips

  function facts(): TipFacts {
    const player = ctx.services.tryGet('player');
    const mode = player?.mode === 'foot' ? 'foot' : player?.mode === 'driving' ? 'driving' : 'other';
    const settings = ctx.services.tryGet('save')?.settings;
    return {
      touch: touchUi.enabled,
      touchLeftHanded: settings?.touchLeftHanded ?? false,
      mode,
      time: playTime,
      walked,
      ran,
      driven,
      nearCar,
      enteredCar,
      heat: ctx.state.heat,
      mapOpened,
      bookOpened,
      zoomChanged,
      jobsDone: ctx.services.tryGet('save')?.data.completedMissions.length ?? 0,
      fenceKnown: !online() && !!ctx.services.tryGet('weapons')?.fence,
      nearFence,
      // Online the law strip is the running commentary; offline the NEXT clipping says "Lose the law"
      // whenever no job is up, and a job's own "lose the law" stage (the first job's chase) says it in
      // the clipping. Either way the tip would be a second voice saying the same thing.
      stolenCar: player?.mode === 'driving' && !!player.vehicle?.stolen && !player.vehicle.ownedByPlayer,
      online: online(),
      onJob: jobRunning(),
      lawShown: online() ? ctx.state.heat > 0 : step?.kind === 'law' || ctx.services.tryGet('missions')?.stageKind === 'escapeHeat',
      keys: { handbrake: bindingLabel(ctx.input.bindings.keys.handbrake), run: bindingLabel(ctx.input.bindings.keys.run) },
    };
  }

  /**
   * A module's card is up that isn't an overlay the UI knows about (the fence counter, Halloran's job
   * card, an online panel): tips and the pointer step aside for it too (belt and braces for `ui.free`).
   */
  let cardUp = false;
  let cardTick = 0;
  function moduleCardUp(): boolean {
    if (cardTick-- <= 0) {
      cardTick = 10;
      cardUp = !!ctx.uiRoot?.querySelector('.wpn-room-layer:not([hidden]), .gw-card, .ol-host.open .ol-panel');
    }
    return cardUp;
  }

  /**
   * The papers the pointer's pill keeps off: the interaction prompt, the tip slip, a subtitle and (on a
   * phone) the law strip, measured every few frames. The pill used to land on the USE prompt and on the
   * tip slip in the band between the thumbs.
   */
  let paperRects: DOMRect[] = [];
  let paperTick = 0;
  function clearOfPapers(p: PointerPlace): PointerPlace {
    if (p.onScreen) return p;
    if (paperTick-- <= 0) {
      paperTick = 6;
      paperRects = [];
      const els = ctx.uiRoot?.querySelectorAll('.hud-prompt:not([hidden]), .ob-tip.show, .hud-say:not(.is-hidden), .hud-law:not([hidden])');
      if (els) for (const e of els) paperRects.push(e.getBoundingClientRect());
    }
    // The pill hangs below the arrow point (onboarding.css .ob-pointer.edge .lab), about 40 px wide.
    const reach = 44;
    let out = p;
    for (const r of paperRects) {
      if (r.width === 0) continue;
      const pillTop = out.y;
      const pillBottom = out.y + 34;
      if (out.x < r.left - reach || out.x > r.right + reach || pillBottom < r.top - 4 || pillTop > r.bottom + 4) continue;
      // Below the paper when pinned to the top band, above it otherwise.
      out = out.y < r.top + r.height / 2 ? { ...out, y: r.bottom + 6 } : { ...out, y: r.top - 40 };
    }
    return out;
  }

  function track(dt: number): void {
    const player = ctx.services.tryGet('player');
    if (!player) return;
    const p = player.pos;
    if (last.ok && player.controlEnabled) {
      const d = Math.hypot(p.x - last.x, p.z - last.z);
      if (d < 20) {
        if (player.mode === 'foot') {
          walked += d;
          if (d > 0.001 && ctx.input.held('run')) ran += dt;
        } else if (player.mode === 'driving') driven += d;
      }
    }
    last.x = p.x;
    last.z = p.z;
    last.ok = true;
    probeTimer -= dt;
    if (probeTimer <= 0) {
      probeTimer = 0.25;
      const car = player.mode === 'foot' ? ctx.services.tryGet('vehicles')?.nearest(p, 7, (v) => !v.isPolice && !v.destroyed && v.occupant === null) : null;
      nearCar = !!car;
      const fence = ctx.services.tryGet('weapons')?.fence;
      nearFence = !!fence && Math.hypot(fence.pos.x - p.x, fence.pos.z - p.z) < 12;
    }
  }

  /**
   * The interaction prompt hangs over its car or door, so near the bottom of the frame it can land in the tip's
   * band: at the Vesper's door the slip covered "USE Break into…" — the one thing to read just then. The slip
   * waits while the two would overlap. Measured every few frames.
   */
  let promptHit = false;
  let promptTick = 0;
  function overPrompt(): boolean {
    if (promptTick-- > 0) return promptHit;
    promptTick = 6;
    promptHit = false;
    const pr = ctx.uiRoot?.querySelector('.hud-prompt:not([hidden])');
    if (!pr || !tipEl) return false;
    const a = pr.getBoundingClientRect();
    // The slip's own box, even while it is hidden (it keeps its place in the layout).
    const b = tipEl.getBoundingClientRect();
    promptHit = a.width > 0 && b.width > 0 && a.left < b.right && a.right > b.left && a.top < b.bottom + 6 && a.bottom > b.top - 6;
    return promptHit;
  }

  function updateTips(dt: number, free: boolean): void {
    if (!tipEl || !tipText) return;
    const f = facts();
    // In a chase, a fight or at speed the tips wait (src/ui/attention.ts: the screen is for the road).
    const busy = !allowed('tip', ui()?.attention ?? 'calm');
    const enabled = !stored.tipsOff && free && !busy && !note && !moduleCardUp();
    // The bottom band's order is subtitle > law strip (on a phone) > tip > radio: while the client is on
    // the line, or the law's running commentary has the band, the slip waits (its time on screen doesn't
    // count) instead of stepping up over the words.
    const u = ui();
    const saying = !!u?.saying || (touchUi.enabled && !!u?.lawUp) || overPrompt();
    const finished = tips.update(f, free && !saying && !busy ? dt : 0, enabled);
    if (finished.length) markTipsDone(finished);
    const text = enabled ? tips.text(f) : null;
    if ((text ?? '') !== tipShown) {
      tipShown = text ?? '';
      if (text) setText(tipText, touchWording(text, touchUi.enabled));
      tipEl.dataset.tip = tips.current?.id ?? '';
    }
    toggle(tipEl, 'show', !!text && !saying);
  }

  // ------------------------------------------------------------------ system

  return {
    id: 'onboarding',
    order: 762,
    init(c) {
      ctx = c;
      const hadRecord = load();
      for (const id of stored.tips) tips.done.add(id);
      const service: OnboardingService = {
        tipsSetting: {
          get: () => !stored.tipsOff,
          set: (on) => {
            stored.tipsOff = !on;
            save();
          },
        },
        next: () => step,
        setWaypoint(w) {
          waypoint = w ? { x: w.x, z: w.z, label: w.label, ...(w.from ? { from: w.from } : {}) } : null;
          waypointAt = clock;
        },
        get goal() {
          return goal;
        },
        get waypoint() {
          return waypoint;
        },
        get introOpen() {
          return !!note;
        },
      };
      c.services.provide('onboarding', service);
      c.hooks.register('onboardingState', () => ({
        intro: noteKind,
        introPage: note?.page ?? null,
        introPending,
        tip: tips.current?.id ?? null,
        tipText: tipShown || null,
        tipOverPrompt: promptHit,
        tipsDone: [...tips.done],
        tipsOn: !stored.tipsOff,
        next: step ? { kind: step.kind, title: step.title, text: step.text, target: step.target ?? null, jobId: step.jobId ?? null } : null,
        waypoint,
        goal,
        pointer: ptrState,
        counters: { walked: +walked.toFixed(1), ran: +ran.toFixed(2), driven: +driven.toFixed(1), time: +playTime.toFixed(1) },
      }));
      c.hooks.register('onboardingNote', (a) => noteAction(a === 'skip' ? 'skip' : 'next'));
      c.hooks.register('setRoute', (x, z, label) => service.setWaypoint(x === undefined || z === undefined ? null : { x, z, label: label ?? 'there', from: 'map' }));
      c.hooks.register('onboardingReset', () => {
        stored = { v: 1, tips: [], tipsOff: false, welcomed: [] };
        tips.done.clear();
        tips.current = null;
        walked = ran = driven = playTime = 0;
        enteredCar = mapOpened = bookOpened = zoomChanged = false;
        save();
      });
      offs.push(
        c.events.on('newGameStarted', () => {
          introPending = true;
          waypoint = null;
          stored.tips = [];
          tips.done.clear();
          tips.current = null;
          walked = ran = driven = playTime = 0;
          enteredCar = mapOpened = bookOpened = zoomChanged = false;
          save();
        }),
        c.events.on('vehicleEntered', (e) => {
          if (e.byPlayer) enteredCar = true;
        }),
        c.events.on('bookOpened', () => (bookOpened = true)),
        c.events.on('settingsChanged', (e) => {
          if (Math.abs(e.settings.cameraZoom - 1) > 1e-3) zoomChanged = true;
        }),
        c.events.on('netJoined', (e) => {
          const who = c.services.tryGet('net')?.session?.character.name ?? '';
          // Automated multi-client scenarios drive the game by keys and hooks: no welcome card in their way
          // unless they ask for it (`?onboarding`).
          const automated = typeof navigator !== 'undefined' && navigator.webdriver === true && !new URLSearchParams(location.search).has('onboarding');
          if (who && !automated && !stored.welcomed.includes(who)) welcomePending = e.name || 'the city';
          waypoint = null;
        }),
      );
      if (!c.uiRoot || typeof document === 'undefined') return;
      // An existing save (no onboarding record yet) has obviously walked, driven and found the map.
      if (!hadRecord && c.services.tryGet('save')?.hasProgress) {
        markTipsDone(BASIC_TIPS);
      }
      layer = el('div', 'bh-ui ob-layer', undefined, c.uiRoot);
      layer.style.pointerEvents = 'none';
      tipEl = el('div', 'ob-tip bh-glass', undefined, layer);
      el('span', 'bh-tag', 'TIP', tipEl);
      tipText = el('span', 'txt', '', tipEl);
      ptr = el('div', 'ob-pointer k-next', undefined, layer);
      const arrow = el('i', 'arrow', undefined, ptr);
      void arrow;
      const lab = el('div', 'lab bh-soot', undefined, ptr);
      ptrLabel = el('span', 'who', '', lab);
      ptrDist = el('b', 'dist', '', lab);
      keyHandler = (e: KeyboardEvent): void => {
        if (!note) return;
        const nextKeys = ['Enter', 'NumpadEnter', 'Space', 'KeyE', 'ArrowRight', 'KeyD'];
        const skipKeys = ['Escape', 'Backspace'];
        if (nextKeys.includes(e.code) || skipKeys.includes(e.code)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (e.repeat) return;
          noteAction(skipKeys.includes(e.code) ? 'skip' : 'next');
        }
      };
      window.addEventListener('keydown', keyHandler, true);
    },
    start() {
      ui()?.addMarkerSource(markerSource);
    },
    update(dt) {
      if (!layer) return;
      const u = ui();
      const started = !!u && u.screen !== 'title' && u.screen !== 'boot' && ctx.state.mode !== 'title';
      toggle(layer, 'off', !started);
      if (!started) return;
      const free = !!u?.free;
      // Notes wait until the HUD is clear (not over the title fade, a result or a menu).
      if (!note && free && introPending && !online()) showIntro();
      if (!note && free && welcomePending && online()) {
        const who = ctx.services.tryGet('net')?.session?.character.name ?? '';
        // A second join while the card was up (a quick reconnect) must not bring it back once it was read.
        if (stored.welcomed.includes(who)) welcomePending = null;
        else showWelcome(welcomePending, who);
      }
      if (note) {
        if (!u?.free && u?.screen !== 'hud') note.finish(true);
        // Pad: A turns the page, B skips.
        if (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function') {
          let a = false;
          let b = false;
          for (const p of navigator.getGamepads()) {
            if (!p?.connected) continue;
            a ||= !!p.buttons[0]?.pressed;
            b ||= !!p.buttons[1]?.pressed;
          }
          if (a && !padPrev.a) noteAction('next');
          if (b && !padPrev.b) noteAction('skip');
          padPrev = { a, b };
        }
      }
      clock += dt;
      if (free) playTime += dt;
      if (u?.screen === 'map') mapOpened = true;
      track(dt);
      // Arrived at a marked place.
      const player = ctx.services.tryGet('player');
      if (waypoint && player && Math.hypot(waypoint.x - player.pos.x, waypoint.z - player.pos.z) < ARRIVE_M) {
        u?.toast(`You’re there: ${waypoint.label}.`, 'good', 2.5);
        waypoint = null;
      }
      step = computeStep();
      updateGoal();
      if (u) {
        // Online the heat plate is the state and the law strip the running commentary: the NEXT clipping
        // does not say "lose the law" a third time (offline it is the only commentary, so it does).
        if (step && !(step.kind === 'law' && online())) {
          const d = step.target && player ? Math.hypot(step.target.x - player.pos.x, step.target.z - player.pos.z) : undefined;
          u.setIdleObjective({ title: step.title, text: step.text, ...(step.hint ? { hint: step.hint } : {}), ...(d !== undefined ? { distance: d } : {}), tag: 'NEXT' });
        } else u.setIdleObjective(null);
      }
      updateTips(dt, free);
      updatePointer();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      if (keyHandler) window.removeEventListener('keydown', keyHandler, true);
      note?.dispose();
      layer?.remove();
    },
  };
}
